<!--
  - Copyright (c) 2021. Trade Research Advisory (Pty) Ltd, P.O.Box 336, Cornwall Hill, Irene, 0178
  - #  This file forms part of the Trade-DSM application frontend.
  - #  https://github.com/Tooblippe/trade-dsm-frontend
  -->

<template>
  <div :key="render_map" class="pa-0 white">
    <v-container fluid class="ma-0 pa-0">
      <v-row class="align-center" no-gutters>
        <v-col cols="1">
          <v-img
            :src="'/flags/' + countryCode.toLowerCase() + '.svg'"
            max-height="50px"
            max-width="50px"
            height="50px"
            width="50px"
          />
        </v-col>
        <v-col cols="10">
          <h4 class="text-left ml-3">
            Home market:
            <span class="font-italic">{{ $store.state.app.selectedHomeCountry }}</span>
          </h4>
        </v-col>
      </v-row>
      <v-row class="align-center" v-if="selectedCountry !== 'none'" no-gutters>
        <v-col cols="1">
          <v-img
            :src="'/flags/' + selectedCountry.toLowerCase() + '.svg'"
            max-height="50px"
            max-width="50px"
            height="50px"
            width="50px"
          />
        </v-col>
        <v-col cols="10">
          <h4 class="text-left ml-3">
            Target market:
            <span class="font-italic">{{ target_market }}</span>
          </h4>
        </v-col>
      </v-row>
    </v-container>

    <template v-if="show_printable" class="ma-0 pa-0 caption">
      <span id="png_placeholder" class="ma-0 pa-0 caption"></span>
    </template>

    <div :key="big_map" id="mpmc_map" class="white">
      <div :key="refresh" class="chartWithOverlay">
        <GChart
          ref="gChart"
          :data="create_country_plot_array"
          :events="chartEvents"
          :options="options"
          :resizeDebounce="0"
          :settings="{
            packages: ['geochart'],
            mapsApiKey: mapsApiKey,
            tooltip: {isHtml: true},
          }"
          class="pa-0 ma-0 white"
          type="GeoChart"
          @ready="chartReady"
        />
        <!--          @change="refresh++"-->
        <!--          :key="refresh"-->

        <!--        // show the Trade-Advisory image-->
        <trade-map-image :show_map_ta_overlay="show_map_ta_overlay" />
      </div>
    </div>

    <v-layout
      v-if="show_map_ta_overlay"
      class="pa-0 mt-0 justify-space-around align-center no-gutters"
      row
      data-html2canvas-ignore
    >
      <v-flex sm6 class=" mt-0 pt-0">
        <v-switch
          v-model="display_mode_var"
          :label="display_mode_var === 'region' ? 'Show bubbles' : 'Highlight countries'"
          class="sm-4 mb-3 ml-lg-5 mt-0 pt-0"
          color="green"
          dense
          flat
          inset
          value="region"
          @change="
            options.displayMode = display_mode_var === 'region' ? 'region' : 'markers'
          "
        ></v-switch>
      </v-flex>
      <v-flex sm4 class=" mt-0 pt-0">
        <v-select
          class="mt-0 pt-0"
          label="Select zoom region"
          :items="[
            {
              text: 'World',
              value: 'world',
            },
            {
              text: 'Africa',
              value: '002',
            },
            {
              text: 'Europe',
              value: '150',
            },
            {
              text: 'Americas',
              value: '019',
            },
            {
              text: 'Northern America',
              value: '021',
            },
            {
              text: 'Caribbean',
              value: '029',
            },
            {
              text: 'Central America',
              value: '013',
            },
            {
              text: 'South America',
              value: '005',
            },
            {
              text: 'Asia',
              value: '142',
            },
            {
              text: 'Oceania',
              value: '009',
            },
          ]"
          @change="options.region = $event"
        >
          //https://en.wikipedia.org/wiki/UN_M49
        </v-select>
      </v-flex>
      <v-flex sm1 class=" mt-0 pt-0">
        <help-link help_id="5" style="float: left" class="mt-0 pt-0" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import {GChart} from 'vue-google-charts';
import vFmt from '@/components/application-level/mixins/vFmt';
import {mapGetters} from 'vuex';
import TradeMapImage from '@/components/trade-dsm-application/result-components/helpers/trade-map-image';
import swal from 'sweetalert';
import helpLink from '@/components/application-level/help-system/help-link';

export default {
  name: 'WorldMap_MPMC',
  mixins: [vFmt],
  components: {
    TradeMapImage,
    GChart,
    helpLink,
  },

  mounted() {
    // this.options.region = this.pad_with_zeroes(this.homeCountry['Main_Region_Code'], 3);
    this.options.region = 'world';
    const results_dict = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
      .combinations.combinations_cube;
    let max_value = 0;
    for (const key in results_dict) {
      if (results_dict[key]['Avg6Major'] > max_value) {
        max_value = results_dict[key]['Avg6Major'];
      }
    }
    this.options.sizeAxis.maxValue = this.plot_max_value;
    this.options.sizeAxis.minValue = -this.plot_max_value / 5;
    window.onresize = () => (this.render_map += 1);
  },
  updated() {
    this.options.sizeAxis.maxValue = this.plot_max_value;
    this.options.sizeAxis.minValue = -this.plot_max_value / 5;
  },
  data() {
    return {
      chart_ready: false,
      refresh: 0,
      chart_handler: {},
      corrections: [],
      selected_c: 'select a bubble',
      selected_code: 'select a bubble',
      inc: 0.5,
      lat: 25.9119,
      long: -24.6463,
      updating: false,
      render_map: 0,
      show_printable: true,
      show_map_ta_overlay: false,
      max_val: 20000,

      mapsApiKey: 'AIzaSyDNbYELN5uXbJpmqIOKBC7AO0aZeazfnM8',
      display_mode_var: 'markers',
      options: {
        region: 'world',
        legend: 'none',
        displayMode: 'markers', //region
        colorAxis: {
          minValue: -this.plot_max_value,
          maxValue: this.plot_max_value,
          colors: ['blue', 'rgb(182, 198, 221)', 'green'],
        },
        sizeAxis: {
          minSize: this.$store.state.app.big_map ? 1 : 1,
          maxSize: this.$store.state.app.big_map ? 8 : 7,
          maxValue: this.plot_max_value,
        },
        backgroundColor: {
          strokeWidth: '0' /* control the countries borders width */,
          stroke: 'lightgray',
        },
        datalessRegionColor: '#cadcf5',
        height: this.$store.state.app.big_map ? 480 : 360, //360
        forceFrame: true,
        enableRegionInteractivity: true,
        magnifyingGlass: {enable: true, zoomFactor: 7.5},
        tooltip: {
          isHtml: true,
          showTitle: false,
        },
      },
      markers: {
        stroke: '#00ff00',
      },
      chartEvents: {
        // When there is a click on the chart
        select: async () => {
          const chart = this.$refs.gChart.chartObject.getSelection();

          // get reference to data
          const chart_id = chart[0]['row'] + 1;

          // get data point 0
          // if region then this point is the country ISO2_Alpha
          // if not region then this point is the CoOrdinate_X
          const country = this.create_country_plot_array[chart_id][0];

          this.lat = this.countriesSearchAndSelectedList.filter(
            v => v['CoOrdinate_X'] === country,
          )[0]['CoOrdinate_X'];

          this.long = this.countriesSearchAndSelectedList.filter(
            v => v['CoOrdinate_X'] === country,
          )[0]['CoOrdinate_Y'];

          this.selected_c = this.countriesSearchAndSelectedList.filter(
            v => v['CoOrdinate_X'] === country,
          )[0]['C_Name'];

          this.selected_code = this.countriesSearchAndSelectedList.filter(
            v => v['CoOrdinate_X'] === country,
          )[0]['C_Code'];
          let country_code = '';
          const enable_select = true; //disabled for now
          if (enable_select) {
            // get a reference to the chart
            const chart = this.$refs.gChart.chartObject.getSelection();

            // get reference to data
            const chart_id = chart[0]['row'] + 1;

            // get data point 0
            // if region then this point is the country ISO2_Alpha
            // if not region then this point is the CoOrdinate_X

            // test to see in which chart view mode we are in
            if (this.display_mode_var === 'region') {
              // the country code is where the ISO2 Alpha is equal to the map value
              const country = this.create_country_plot_array[chart_id][0];
              country_code = this.countriesSearchAndSelectedList.filter(
                v => v['C_Code_ISO_Alpha_2'] === country,
              )[0]['C_Code'];
            } else {
              // the country code is where the CoOrdinateX is equal to the map value
              country_code = this.countriesSearchAndSelectedList.filter(
                v => v['CoOrdinate_X'] === country,
              )[0]['C_Code'];
            }
            if (
              country_code !==
                this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
                  .home_market &&
              country_code !==
                this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections
                  .markets.C_Code[0]
            ) {
              this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets.C_Code = [];
              this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets.Main_Region = [];
              this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets.Sub_Region.length = [];
              this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets.Trade_Group.length = [];

              this.$set(
                this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections
                  .markets.C_Code,
                0,
                country_code,
              );

              this.$store.commit('set_product_search_term', country_code);

              await this.update_results_cube();
            }
          }
        },
      },
    };
  },

  watch: {
    big_map(v) {
      if (v) {
        this.options.height = 800;
      } else {
        this.options.height = 340;
      }
    },
    plot_max_value(old_v) {
      this.options.sizeAxis.maxValue = old_v;
      // console.log('max value changed');
      // console.log(new_v);
      this.refresh++;
    },
    create_country_plot_array() {
      this.refresh = Math.random();
    },
  },

  computed: {
    ...mapGetters(['app_data', 'selectionCounts', 'countriesSelectorList']),

    big_map() {
      return this.$store.state.app.big_map;
    },

    plot_max_value() {
      // what is the max value for the map  scaling
      const results_dict = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
        .combinations.combinations_cube;
      let max_value = 0;

      for (const key in results_dict) {
        if (results_dict[key]['Avg6Major'] > max_value) {
          max_value = results_dict[key]['Avg6Major'];
          // console.log(max_value);
        }
      }

      return max_value;
    },

    plot_min_value() {
      // what is the max value for the map  scaling
      const results_dict = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
        .combinations.combinations_cube;
      let min_value = this.plot_max_value;

      for (const key in results_dict) {
        if (results_dict[key]['Avg6Major'] < min_value) {
          min_value = results_dict[key]['Avg6Major'];
        }
      }

      return min_value;
    },
    create_country_plot_array() {
      let plot_array = [];
      if (this.chart_ready) {
        // Set up array headers
        if (this.options.displayMode === 'region') {
          plot_array = [['Country', 'Value', 'Size', {role: 'tooltip', p: {html: true}}]];
          // add home market
          plot_array.push([
            this.homeCountry['C_Code_ISO_Alpha_2'], // region location
            -this.plot_max_value, // region color
            null,
            `<p>Home Market ${this.homeCountry['C_Name']}(${
              this.homeCountry['C_Code']
            })</p>`,
          ]);
        } else {
          plot_array = [
            ['Lat', 'Lng', 'Color', 'Val', {role: 'tooltip', p: {html: true}}],
          ];
          plot_array.push([
            this.homeCountry['CoOrdinate_X'], //bubble lat
            this.homeCountry['CoOrdinate_Y'], //bubble long
            -this.plot_max_value, //bubble color
            this.plot_max_value, // bubble size
            `<h4 style="text-align: center">Home Market ${this.homeCountry['C_Name']}(${
              this.homeCountry['C_Code']
            })</h4>`,
          ]);
        }

        const results_dict = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
          .combinations.combinations_cube;
        if (results_dict) {
          this.countriesSearchAndSelectedList.forEach(v => {
            if (v.in_cube === true) {
              if (this.options.displayMode === 'region') {
                if (
                  v['In_Results'] &&
                  v.in_cube &&
                  v.C_Code_ISO_Alpha_2 !== this.homeCountry['C_Code_ISO_Alpha_2']
                ) {
                  // const color = v.C_Code_Selected ? 2 : 3;
                  plot_array.push([
                    v['C_Code_ISO_Alpha_2'],
                    this.plot_max_value,
                    0,
                    this.market_map_str(results_dict, v),
                  ]);
                }
              } else {
                // bubbles
                if (
                  v['In_Results'] &&
                  v.in_cube &&
                  v.C_Code_ISO_Alpha_2 !== this.homeCountry['C_Code_ISO_Alpha_2'] &&
                  results_dict.hasOwnProperty(v.C_Code)
                ) {
                  const blob = results_dict[v.C_Code]['Avg6Major'];

                  plot_array.push([
                    v['CoOrdinate_X'], // bubble lat
                    v['CoOrdinate_Y'], // bubble long
                    this.plot_max_value, // bubble color
                    blob, // bubble size
                    this.market_map_str(results_dict, v),
                  ]);
                }
              }
            } else {
              // just add the name to the hover / bubbles

              if (
                v['C_Code'] !== this.homeCountry['C_Code'] &&
                this.current_report_to_run !== 'MP1C'
              ) {
                if (this.options.displayMode !== 'region') {
                  plot_array.push([
                    v['CoOrdinate_X'],
                    v['CoOrdinate_Y'],
                    0,
                    0,
                    `${v['C_Name']} (${v['C_Code']}) <br> No results`,
                  ]);
                } else {
                  plot_array.push([
                    v['C_Code_ISO_Alpha_2'],
                    0,
                    0,
                    `${v['C_Name']} (${v['C_Code']}) <br> No results`,
                  ]);
                }
              }
            }
          });
        }
      }
      return plot_array;
    },
    homeCountry() {
      return this.$store.getters.countriesSelectorList.filter(
        country => country['C_Name'] === this.$store.state.app.selectedHomeCountry,
      )[0];
    },
    targetCountry() {
      return this.$store.getters.countriesSelectorList.filter(
        country =>
          country['C_Name'] ===
          this.$store.getters.dsmFilterSettings['Destination_Country'],
      )[0];
    },
    productAndCountrySelected() {
      return (
        this.$store.state.tradeDSM.dsm_filter_setttings['Destination_Market'].length >
          0 && this.$store.state.tradeDSM.dsm_filter_setttings['HS6_Code'].length > 0
      );
    },
    countryCode() {
      return this.$store.getters.countriesSelectorList
        .filter(
          country => country['C_Name'] === this.$store.state.app.selectedHomeCountry,
        )
        .map(v => v['C_Code_ISO_Alpha_2'])[0];
    },
    target_market() {
      if (
        // if one selected
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
          .C_Code.length === 1
      ) {
        return this.codeToMarket(
          this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
            .C_Code[0],
        );
      } else {
        return '';
      }
    },
    selectedCountry() {
      if (
        // if one selected
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
          .C_Code.length === 1
      ) {
        return this.$store.getters.countriesSelectorList
          .filter(
            country =>
              country['C_Code'] ===
              this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections
                .markets.C_Code[0],
          )
          .map(v => v['C_Code_ISO_Alpha_2'])[0];
      } else {
        return 'none';
      }
    },
  },
  untapped_potential() {
    try {
      return (
        "<p style = 'white-space: nowrap;'> Target Market <br>Untapped Potential: <br>" +
        this.app_data.exchange_rate['Currency'].slice(0, 3) +
        ' ' +
        this.vFmt(
          (this.selectionCounts.results['trade_value'] /
            this.app_data.value_mulitplier['divider']) *
            this.app_data.exchange_rate['TW_FX_Rate'],
          2,
        ) +
        '' +
        this.app_data.value_mulitplier['prefix'] +
        '</p>'
      );
    } catch (e) {
      return '';
    }
  },

  methods: {
    market_map_str(results_dict, v) {
      let rank_text = '';
      if (
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.query_results['cube']
          .length > 0 &&
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
          .report_type_to_generate === '1PMC'
      ) {
        let cube_values = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
          .query_results['cube'];

        let rank = cube_values.filter(q => q['C_Code_x'] === v.C_Code)[0];

        if (rank !== undefined) {
          rank_text = `Rank ${rank['Market_Rank']} out of ${rank['Market_REO_Count']}`;
        } else {
          rank_text = '';
          swal.close();
        }
      }

      return `<div style="width: 300px">
                     Country:  <b>${v.C_Name} (${v.C_Code})</b> <br>
                     Untapped potential: <b>${this.totalValueString(
                       results_dict[v.C_Code]['Avg6Major'] * 1000,
                     )}</b> <br>
                     Number of products: <b>${results_dict[v.C_Code]['count']}</b><br>
                     ${rank_text}
                   </div>
                   `;
    },
    chartReady: function() {
      this.show_map_ta_overlay = true;
      this.chart_ready = true;
    },
    showButtons: () => {
      if (this.homeCountry['Main_Region_Code'] !== undefined) {
        return (
          this.homeCountry['Main_Region_Code'] !== '-' &&
          this.homeCountry['Main_Region_Code']
        );
      } else {
        return false;
      }
    },
  },
};
</script>

<style scoped>
p {
  background-color: cyan;
}

.google-visualization-tooltip {
  width: 300px;
  background-color: red;
}

.chartWithOverlay {
  position: relative;
  background: white;
}

circle {
  stroke-width: 30px !important;
}

.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 50%;
}
</style>
